import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
    IFieldValue,
    UpdateFieldValue,
    updateFieldValue,
} from 'Scripts/Slices/loginPage';
import { IRootState } from 'Scripts/store';

import { useChangePasswordMutation, useLoginMutation } from 'Services/loginApi';
import { logintUserDataLayerPush } from 'Utils/dataLayerPushService';
import { setVoyadoContactId } from 'Services/voyadoApi';

import CtaButton from 'Components/CtaButton';
import FormBox from 'Components/Forms/FormBox';
import RenderFormField, {
    handleFormPost,
    validateFormFields,
    ValidationSchema,
} from 'Components/Forms/RenderFormField';
import Markup from 'Components/Markup';

const LoginPage = () => {
    const dispatch = useDispatch();
    const [formValidation, setFormValidation] = useState<ValidationSchema>([]);

    const [
        login,
        {
            isLoading: loginResultIsLoading,
            error: loginResultError,
            data: loginResultData,
        },
    ] = useLoginMutation();
    const [
        changePassword,
        {
            isLoading: changePasswordResultIsLoading,
            error: changePasswordResultError,
            data: changePasswordResultData,
        },
    ] = useChangePasswordMutation();

    const { login: loginPage, changePassword: changePasswordPage } =
        useSelector((state: IRootState) => state.loginPage);

    const {
        inputFields,
        canLogin,
        loginPageTitle,
        forgottenPasswordLink,
        loginButton,
        newCustomerTitle,
        newCustomerContent,
        linkToRegisterPage,
        changePasswordTitle,
        changePasswordButton,
        channelUrl,
        loginUserAlreadyLoggedInText,
        logoutButtonText,
    } = loginPage;
    const { inputFields: changePasswordInputFields } = changePasswordPage;
    const isChangePasswordView = changePasswordInputFields.length > 0;

    const handleInputChange = (
        data: IFieldValue,
        key: UpdateFieldValue['key']
    ) => {
        dispatch(updateFieldValue({ data, key }));
    };

    const handleOnsubmit = (
        e: React.FormEvent<HTMLFormElement>,
        key: UpdateFieldValue['key']
    ) => {
        e.preventDefault();
        if (key === 'login') {
            if (validateFormFields(inputFields, setFormValidation)) {
                login({ form: e.currentTarget });
                logintUserDataLayerPush();
            }
        } else if (key === 'changePassword') {
            if (
                validateFormFields(changePasswordInputFields, setFormValidation)
            ) {
                changePassword({ form: e.currentTarget });
            }
        }
    };

    useEffect(() => {
        if (loginResultData && loginResultData.contactId) {
            setVoyadoContactId(loginResultData.contactId);
        }

        handleFormPost(loginResultData, loginResultError);
    }, [loginResultData, loginResultError]);

    useEffect(() => {
        handleFormPost(changePasswordResultData, changePasswordResultError);
    }, [changePasswordResultData, changePasswordResultError]);

    return (
        <div className="h-full w-full flex-grow bg-white">
            <div className="mx-auto max-w-content pb-4 lg:px-14">
                <div
                    className={`mx-auto mt-9 grid w-full gap-6 md:max-w-form ${
                        canLogin || !isChangePasswordView
                            ? 'md:grid-cols-2'
                            : ''
                    }`}
                >
                    {canLogin ? (
                        <>
                            <div className="md:col-span-1 md:col-start-1 md:col-end-1">
                                <h1 className="mx-4.5 font-montserrat text-2xl font-medium text-black md:col-span-2 md:mx-4.5 md:mb-4 lg:col-span-1 lg:mx-7">
                                    {loginPageTitle}
                                </h1>
                                <FormBox
                                    loading={
                                        loginResultIsLoading ||
                                        changePasswordResultIsLoading
                                    }
                                >
                                    {isChangePasswordView ? (
                                        <>
                                            <h1 className="mx-4.5 font-montserrat text-2xl font-medium text-black md:col-span-2 md:mx-4.5 md:mb-4 lg:col-span-1 lg:mx-7">
                                                {changePasswordTitle}
                                            </h1>
                                            <form
                                                onSubmit={e =>
                                                    handleOnsubmit(
                                                        e,
                                                        'changePassword'
                                                    )
                                                }
                                                className="md:max-w-md"
                                                method="post"
                                            >
                                                <ul className="flex flex-col">
                                                    {changePasswordInputFields.map(
                                                        (field, index) => (
                                                            <RenderFormField
                                                                key={`
                                                             ${field.name}${index}`}
                                                                field={field}
                                                                handleInputChange={e =>
                                                                    handleInputChange(
                                                                        e,
                                                                        'changePassword'
                                                                    )
                                                                }
                                                                form={
                                                                    changePasswordInputFields
                                                                }
                                                                validation={
                                                                    formValidation
                                                                }
                                                                as="li"
                                                            />
                                                        )
                                                    )}
                                                    <li>
                                                        <CtaButton className="mb-3 w-full py-2.5 text-xl font-medium">
                                                            {
                                                                changePasswordButton
                                                            }
                                                        </CtaButton>
                                                    </li>
                                                </ul>
                                            </form>
                                        </>
                                    ) : (
                                        <form
                                            onSubmit={e =>
                                                handleOnsubmit(e, 'login')
                                            }
                                            className="md:max-w-md"
                                            method="post"
                                        >
                                            <ul className="flex flex-col">
                                                {inputFields.map(
                                                    (field, index) => (
                                                        <RenderFormField
                                                            key={index}
                                                            loading={
                                                                loginResultIsLoading ||
                                                                changePasswordResultIsLoading
                                                            }
                                                            field={field}
                                                            handleInputChange={e =>
                                                                handleInputChange(
                                                                    e,
                                                                    'login'
                                                                )
                                                            }
                                                            form={inputFields}
                                                            validation={
                                                                formValidation
                                                            }
                                                            as="li"
                                                        />
                                                    )
                                                )}
                                                <li>
                                                    <CtaButton
                                                        disabled={
                                                            loginResultIsLoading ||
                                                            changePasswordResultIsLoading
                                                        }
                                                        className="mb-3 w-full py-2.5 text-xl font-medium"
                                                        onClick={() => {}}
                                                    >
                                                        {loginButton}
                                                    </CtaButton>
                                                    <a
                                                        href={
                                                            forgottenPasswordLink?.href
                                                        }
                                                    >
                                                        {
                                                            forgottenPasswordLink?.text
                                                        }
                                                    </a>
                                                </li>
                                            </ul>
                                        </form>
                                    )}
                                </FormBox>
                            </div>
                            {!isChangePasswordView && (
                                <div className="px-3 md:col-span-1 md:col-start-2 md:col-end-2 md:row-start-1 md:px-0">
                                    <h2 className="mb-2.5 font-montserrat text-xl font-medium text-black">
                                        {newCustomerTitle}
                                    </h2>
                                    <FormBox>
                                        <Markup
                                            cssClass="mb-5"
                                            html={newCustomerContent?.value}
                                        />
                                        <CtaButton
                                            className="w-full py-2.5 text-xl font-medium"
                                            as="a"
                                            href={linkToRegisterPage?.href}
                                        >
                                            {linkToRegisterPage?.text}
                                        </CtaButton>
                                    </FormBox>
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="md:max-w-md">
                            <FormBox>
                                <p className="mb-5">
                                    {loginUserAlreadyLoggedInText}
                                </p>
                                <CtaButton
                                    className="w-full py-2.5 text-xl font-medium"
                                    as="a"
                                    href={`${channelUrl}.Logout`}
                                >
                                    {logoutButtonText}
                                </CtaButton>
                            </FormBox>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default LoginPage;
